import { basicInfoAtom, cartAtom } from "@/atoms";
import { PrizeGameOffer } from "@/components/marketing/PrizeGameOffer";
import { useAbandonedCartReminder } from "@/hooks/use-abandoned-cart-reminder";
import { AuthSheet, BirthdayPromptSheet, StoreClosedModal } from "@/components/modals";
import { HamburgerMenu } from "@/components/navigations";
import { ActiveOrderBar } from "@/components/orders";
import { FloatingSupportButton } from "@/components/support/FloatingSupportButton";
import { useHamburgerMenuConfig } from "@/hooks/use-hamburger-menu-config";
import { useMetaPixelPageViews } from "@/lib/meta-pixel/use-meta-pixel-page-views";
import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { tableAtom } from "@/atoms/table";
import { getMerchantId } from "@/lib/company-storage";
import { hydratePendingCouponForThisVisit } from "@/lib/pending-coupon";
import { NotFound } from "../components/not-found";
import { Addresses } from '../pages/addresses';
import { Auth } from '../pages/auth';
import { Cart } from '../pages/cart';
import {
  CheckoutEntry,
  CheckoutLayout,
  ExtrasStep,
  OrderConfirmedPage,
  PaymentStep,
  ReviewStep,
  ScheduleStep,
  ServiceTypeStep,
  TimingStep,
} from '../pages/cart/checkout';
import { Home } from '../pages/home';
import { Info } from '../pages/home/info';
import { LoyaltyPage } from '../pages/Loyalty';
import { MenuPage } from '../pages/menu';
import NewAddress from '../pages/NewAddress';
import NewProfileAddress from '../pages/NewProfileAddress';
import Orders from '../pages/Orders';
import { Profile } from '../pages/Profile';
import { OrderPage } from '../pages/profile/orders/order-id/page';
import { OrderProblemPage } from '../pages/profile/orders/order-id/problem/page';
import TrackOrderPage from '../pages/track/TrackOrderPage';
import { CompleteEmailSheet } from '../components/profile/CompleteEmailSheet';

/**
 * Keeps the ?table=<id> slug in the URL while the customer is in dine-in
 * (table) mode, so the table context survives reloads and shared links and the
 * URL reflects the order type across every route (menu -> cart -> checkout).
 */
function TableSlugSync() {
  const table = useAtomValue(tableAtom)
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (!table) return
    if (searchParams.get("table") === table.id) return
    const next = new URLSearchParams(searchParams)
    next.set("table", table.id)
    setSearchParams(next, { replace: true })
  }, [table, location.pathname, searchParams, setSearchParams])

  return null
}

/**
 * On the first load of this visit: drop any leftover pending coupon, then
 * apply `?cupom=` if the URL still has one. The slug stays in the URL so a
 * shared link reapplies on F5. A showcase tap after this is session-only
 * (cleared on the next full load unless the slug is present).
 */
function CouponQuerySync() {
  const [searchParams] = useSearchParams()

  useEffect(() => {
    hydratePendingCouponForThisVisit(searchParams.toString(), getMerchantId())
  }, [searchParams])

  return null
}

/**
 * `/cart/restore?mc=..&s=..` link of an abandoned-cart reminder: the cart lives on this device, so a non-empty
 * bag goes to the cart and anything else to the home, keeping `mc` so the attribution is captured.
 */
function RestoreCartRedirect() {
  const cart = useAtomValue(cartAtom)
  const { search } = useLocation()
  const mc = new URLSearchParams(search).get("mc")
  const target = cart.items.length > 0 ? "/cart" : "/"
  return <Navigate to={mc ? `${target}?mc=${encodeURIComponent(mc)}` : target} replace />
}

export const Router = () => {
  const basicInfo = useAtomValue(basicInfoAtom)
  const menuConfig = useHamburgerMenuConfig()
  useMetaPixelPageViews()
  useAbandonedCartReminder()

  if (!basicInfo) throw new Error("Basic info not found")

  return (
    <div className="w-full max-w-shell m-auto">
      <TableSlugSync />
      <CouponQuerySync />
      {!menuConfig.hideMenu && (
        <HamburgerMenu
          basicInfo={basicInfo}
          showBackButton={menuConfig.showBackButton}
          title={menuConfig.title}
          className={menuConfig.customClassName}
          backButtonRoute={menuConfig.backButtonRoute}
        />
      )}
      <ActiveOrderBar hideMenuAbove={menuConfig.hideMenu} />
      <FloatingSupportButton />
      <CompleteEmailSheet />
      <PrizeGameOffer />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/info" element={<Info />} />

        <Route path="/menu" element={<MenuPage />} />

        <Route path="/cart" element={<Cart />} />
        <Route path="/cart/restore" element={<RestoreCartRedirect />} />
        <Route path="/cart/checkout" element={<CheckoutLayout />}>
          <Route index element={<CheckoutEntry />} />
          <Route path="timing" element={<TimingStep />} />
          <Route path="service" element={<ServiceTypeStep />} />
          <Route path="schedule" element={<ScheduleStep />} />
          <Route path="payment" element={<PaymentStep />} />
          <Route path="extras" element={<ExtrasStep />} />
          <Route path="review" element={<ReviewStep />} />
        </Route>
        <Route path="/cart/order-confirmed/:orderId" element={<OrderConfirmedPage />} />
        <Route path="/cart/confirm-order" element={<Navigate to="/cart/checkout" replace />} />
        <Route path="/cart/new-address" element={<NewAddress />} />
        <Route path="/cart/profile-data" element={<Profile />} />

        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/addresses" element={<Addresses />} />
        <Route path="/profile/addresses/new" element={<NewProfileAddress />} />
        <Route path="/profile/auth" element={<Auth />} />
        <Route path="/profile/loyalty" element={<LoyaltyPage />} />
        <Route path="/profile/orders" element={<Orders />} />
        <Route path="/profile/orders/:id" element={<OrderPage />} />
        <Route path="/p/:token" element={<TrackOrderPage />} />
        <Route
          path="/profile/orders/:id/problem"
          element={<OrderProblemPage />}
        />

        <Route path="*" element={<NotFound />} />
      </Routes>

      <StoreClosedModal />

      <AuthSheet />
      <BirthdayPromptSheet />
    </div>
  )
}